import api from 'services/api';
import { array, bool, enforce, nullable, num, numeric, object, runSchema, str, unknownValue } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { recurrenceSchema, type Recurrence } from './types';

// GET /recurrence_definitions — the list read (overview). Backend:
// `RecurrenceDefinitionService.list`, returning `{ nodes, pagination, config,
// filtersConfig }`.
//
// `RecurrenceDefinitionRow` is a SEPARATE type from the single-GET
// `RecurrenceDefinition`, even though most fields match: the two come from
// different backend service methods (equal-by-coincidence, not identity). The
// list additionally carries `recurrence_display` — the backend's pre-formatted
// rule summary for the list column, which the full-detail read has no use for.
export interface RecurrenceDefinitionRow {
  readonly recurrence_definition_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zone_name: string | null;
  readonly resubmit_on_error: boolean;
  readonly recurrences: readonly Recurrence[];
  // Backend's pre-formatted summary of the rules, for the list column (list-only).
  readonly recurrence_display: string;
  readonly folder_id: number | null;
  readonly folder_path: FolderPath | null;
  readonly created_at: string;
  readonly updated_at: string;
}

interface Pagination {
  readonly total_records: number;
  readonly total_pages: number;
  readonly current_page: number;
  readonly limit: number;
  readonly offset: number;
}

export interface RecurrenceDefinitionsPage {
  readonly nodes: readonly RecurrenceDefinitionRow[];
  readonly pagination: Pagination;
  // List header / filter config. Typed opaquely for now — no in-scope consumer
  // reads them (the list page still uses the un-migrated slice). Tighten (and
  // decide shared-vs-own) when that slice migrates.
  readonly config: readonly unknown[];
  readonly filtersConfig: readonly unknown[];
}

const rowSchema = object<RecurrenceDefinitionRow>({
  recurrence_definition_id: num(),
  name: str(),
  description: nullable(str()),
  zone_name: nullable(str()),
  resubmit_on_error: bool(),
  recurrences: array(recurrenceSchema),
  recurrence_display: str(),
  folder_id: nullable(num()),
  folder_path: nullable(folderPathSchema),
  created_at: str(),
  updated_at: str(),
});

// COUNT-derived totals arrive as numeric strings from Postgres; `numeric()`
// normalizes both number and string.
const paginationSchema = object<Pagination>({
  total_records: numeric(),
  total_pages: numeric(),
  current_page: numeric(),
  limit: numeric(),
  offset: numeric(),
});

const pageSchema = object<RecurrenceDefinitionsPage>({
  nodes: array(rowSchema),
  pagination: paginationSchema,
  config: array(unknownValue()),
  filtersConfig: array(unknownValue()),
});

export const getRecurrenceDefinitions = async (params: Record<string, unknown> = {}): Promise<RecurrenceDefinitionsPage> => {
  const res = await api.get('/recurrence_definitions', { params });
  const r = runSchema(pageSchema, res.data, 'getRecurrenceDefinitions');
  if (!r.ok) {
    throw new Error('getRecurrenceDefinitions: response failed validation');
  }
  return enforce(r.value, r.warnings, ['nodes[*].recurrence_definition_id']);
};
